import { useState, useEffect, FormEvent } from 'react';
import { Plus, DollarSign, AlertTriangle, Pencil, Trash2 } from 'lucide-react';
import api from '../services/api';
import { PageLoading } from '../components/common/Loading';

interface Income {
  id: number;
  name: string;
  amount: number;
  frequency: 'weekly' | 'biweekly' | 'semimonthly' | 'monthly' | 'annual';
  monthly_amount?: number;
  start_date: string;
  created_at: string;
}

const FREQUENCY_LABELS: Record<string, string> = {
  weekly: 'Weekly',
  biweekly: 'Every 2 Weeks',
  semimonthly: 'Twice a Month',
  monthly: 'Monthly',
  annual: 'Annually',
};

export default function Income() {
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingIncome, setEditingIncome] = useState<Income | null>(null);
  const [monthlyTotal, setMonthlyTotal] = useState(0);

  useEffect(() => {
    loadIncomes();
  }, []);

  const loadIncomes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ incomes: Income[]; monthly_total: number }>('/incomes');
      setIncomes(response.incomes || []);
      setMonthlyTotal(response.monthly_total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load incomes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this income source?')) return;

    try {
      await api.delete(`/incomes/${id}`);
      await loadIncomes();
    } catch (err: any) {
      setError(err.message || 'Failed to delete income');
    }
  };

  const handleEdit = (income: Income) => {
    setEditingIncome(income);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingIncome(null);
  };

  const handleFormSubmit = async () => {
    await loadIncomes();
    handleFormClose();
  };

  if (isLoading) {
    return <PageLoading />;
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-header tracking-tight">Income</h1>
          <p className="mt-1 text-text-muted">Manage your income sources</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn btn-primary shadow-glow">
          <Plus className="w-4 h-4" />
          Add income
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {/* Monthly Total */}
      <div className="card bg-primary/10 border-primary/20 shadow-glow">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-text-muted">Total monthly income</p>
            <p className="text-3xl font-bold text-text-header mt-2">{formatCurrency(monthlyTotal)}</p>
          </div>
          <div className="h-12 w-12 rounded-xl bg-primary/15 border border-primary/20 flex items-center justify-center">
            <DollarSign className="h-6 w-6 text-primary" />
          </div>
        </div>
      </div>

      {/* Income List */}
      {incomes.length === 0 ? (
        <div className="card text-center">
          <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-4 border border-primary/20">
            <DollarSign className="h-6 w-6 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-text-header">No income sources</h3>
          <p className="mt-2 text-sm text-text-muted">Add your income to start budgeting.</p>
          <button onClick={() => setShowForm(true)} className="btn btn-primary mt-6 inline-flex">
            <Plus className="w-4 h-4" />
            Add your first income
          </button>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="p-6 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-semibold text-text-header">Income sources</h2>
            <p className="text-sm text-text-muted">{incomes.length} total</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-border bg-background-card/50">
                  <th className="table-header">Source</th>
                  <th className="table-header">Frequency</th>
                  <th className="table-header text-right">Amount</th>
                  <th className="table-header text-right">Monthly</th>
                  <th className="table-header text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {incomes.map((income) => (
                  <tr key={income.id} className="table-row">
                    <td className="table-cell">
                      <div className="text-sm font-medium text-text-header">{income.name}</div>
                      <div className="text-xs text-text-muted mt-1">
                        Start: {new Date(income.start_date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className="px-2.5 py-1 text-xs rounded-full bg-background-subtle border border-border text-text-body">
                        {FREQUENCY_LABELS[income.frequency]}
                      </span>
                    </td>
                    <td className="table-cell text-right text-text-header">{formatCurrency(income.amount)}</td>
                    <td className="table-cell text-right font-medium text-success">
                      {formatCurrency(income.monthly_amount || income.amount)}
                    </td>
                    <td className="table-cell text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(income)}
                          className="btn btn-secondary px-3 py-2"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(income.id)}
                          className="btn btn-danger px-3 py-2"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <IncomeFormModal income={editingIncome} onClose={handleFormClose} onSubmit={handleFormSubmit} />
      )}
    </div>
  );
}

interface IncomeFormModalProps {
  income: Income | null;
  onClose: () => void;
  onSubmit: () => void;
}

function IncomeFormModal({ income, onClose, onSubmit }: IncomeFormModalProps) {
  const [formData, setFormData] = useState<{
    name: string;
    amount: string;
    frequency: 'weekly' | 'biweekly' | 'semimonthly' | 'monthly' | 'annual';
    start_date: string;
  }>({
    name: income?.name || '',
    amount: income?.amount?.toString() || '',
    frequency: income?.frequency || 'monthly',
    start_date: income?.start_date || new Date().toISOString().split('T')[0],
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!formData.start_date) {
      setError('Start date is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        name: formData.name.trim(),
        amount,
        frequency: formData.frequency,
        start_date: formData.start_date,
      };

      if (income) {
        await api.put(`/incomes/${income.id}`, payload);
      } else {
        await api.post('/incomes', payload);
      }

      onSubmit();
    } catch (err: any) {
      setError(err.message || 'Failed to save income');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md rounded-card bg-background-card/90 backdrop-blur-xl border border-border shadow-glow overflow-hidden">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-text-header">{income ? 'Edit income' : 'Add income'}</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Income source name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input-field"
              placeholder="e.g., Primary job, Freelance"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Amount (per pay period)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="input-field pl-7"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Pay frequency</label>
            <select
              value={formData.frequency}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  frequency: e.target.value as 'weekly' | 'biweekly' | 'semimonthly' | 'monthly' | 'annual',
                })
              }
              className="input-field"
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every 2 Weeks</option>
              <option value="semimonthly">Twice a Month</option>
              <option value="monthly">Monthly</option>
              <option value="annual">Annually</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-muted mb-2">Start date</label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              className="input-field"
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn btn-primary shadow-glow">
              {isSubmitting ? 'Saving…' : income ? 'Update' : 'Add income'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
